// stores/masterUserStore.js
'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};
export const revalidateMasterUserList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/user/search');
};

export const buildUserSearchPayload = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const fb = new FilterBuilder()
    .setPaging(start, rowsPerPage)
    .addJoin('company', ['name', 'pic_mobile_no', 'address', 'pic_email', 'pic_phone_no', 'code', 'phone1', 'fax1', 'website'])
    .setOrder(['id'], false);

  if (searchValue && searchValue.trim() !== '') {
    fb.like(searchKey, searchValue, 'AND');
  }

  if (companyId) {
    if (companyId === 'ALL') {
      // Bypass company filter
    } else {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    }
  } else {
    fb.filterByCompany();
  }
  return fb.build();
};

export const useMasterUserList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const payload = useMemo(
    () => buildUserSearchPayload(page, rowsPerPage, searchKey, searchValue, companyId),
    [page, rowsPerPage, searchKey, searchValue, companyId]
  );

  const key = useMemo(() => ['/user/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.totalRecord ?? 0,
    error,
    isLoading
  };
};
export const useMasterUserAllList = (page, rowsPerPage, searchKey, searchValue, companyId = 'ALL') => {
  const payload = useMemo(
    () => buildUserSearchPayload(page, rowsPerPage, searchKey, searchValue, companyId),
    [page, rowsPerPage, searchKey, searchValue, companyId]
  );

  const key = useMemo(() => ['/user/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.totalRecord ?? 0,
    error,
    isLoading
  };
};

export const fetchUserById = async (id) => {
  if (id == null) throw new Error('ID is required to fetch user');
  try {
    const fb = new FilterBuilder()
      .setPaging(0, 1)
      .addJoin('company', ['name', 'pic_mobile_no', 'address', 'pic_email', 'pic_phone_no', 'code', 'phone1', 'fax1', 'website'])
      .equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/user/search${getTzParam()}`, payload);

    const resData = response?.data;
    const actualData = resData?.data ?? resData;

    if (Array.isArray(actualData)) {
      return actualData.length > 0 ? actualData[0] : null;
    }

    return actualData || null;
  } catch (error) {
    console.error(`Failed to fetchUserById (ID: ${id}):`, error?.response?.data ?? error);
    throw error;
  }
};

export const getUserById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.get(`/user/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user by ID:', error);
    throw error;
  }
};

export const getUserList = async () => {
  try {
    const response = await fleetApi.get(`/user/list${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user list:', error?.response?.data ?? error);
    throw error;
  }
};

export const loginUser = async (credentials = {}) => {
  try {
    const response = await fleetApi.post(`/user/login${getTzParam()}`, credentials);
    return response.data;
  } catch (error) {
    console.error('Login error:', error?.response?.data ?? error);
    throw error;
  }
};

export const refreshUserToken = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/user/refresh-token/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Refresh token error:', error?.response?.data ?? error);
    throw error;
  }
};

export const createUser = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/user/new${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }

    revalidateMasterUserList();
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateUser = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/user/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterUserList();
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteUser = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/user/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterUserList();
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error?.response?.data ?? error);
    throw error;
  }
};

export const uploadUserFile = async (id, fileFormData /* FormData instance */) => {
  if (id == null) throw new Error('id is required');
  if (!(fileFormData instanceof FormData)) throw new Error('fileFormData must be FormData');

  try {
    const response = await fleetApi.post(`/user/uploadfile/${id}`, fileFormData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterUserList();
    return response.data;
  } catch (error) {
    console.error('Error uploading user file:', error?.response?.data ?? error);
    throw error;
  }
};

export const getUserImage = async (id, asBlob = true) => {
  if (id == null) throw new Error('id is required');
  try {
    // default ambil sebagai blob (gambar)
    const response = await fleetApi.get(`/user/images/${id}${getTzParam()}`, {
      responseType: asBlob ? 'blob' : 'arraybuffer'
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user image:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteUserImage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/user/images/${id}${getTzParam()}`);

    revalidateMasterUserList();
    return response.data;
  } catch (error) {
    console.error('Error deleting user image:', error?.response?.data ?? error);
    throw error;
  }
};
